import Toast from "react-native-toast-message";
import * as React from "react";
import { Platform, ScrollView, StyleSheet, Text } from "react-native";
import Colors from "../../constants/Colors";
import { Input, View } from "../../components/Themed";
import { useForm } from "react-hook-form";
import { useRoute } from "@react-navigation/native";
import InputFooter from "../../components/InputFooter";
import GlobalContext from "../../context/Global";
import DateTimeSection from "../../components/DateTimeSection";

type Props = {
  show: boolean;
  openHouse: any;
  onSubmit: (data: any) => void | Promise<void>;
  attendeeCount: number;
};

export default function EndOpenHouseModal() {
  const { isTablet } = React.useContext(GlobalContext);

  const route = useRoute();
  const { onSubmit, openHouse, attendeeCount } = route?.params as Props;
  const [saveDisabled, setSaveDisabled] = React.useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
  } = useForm();

  const startTime = watch("startTime");
  const endTime = watch("endTime");

  React.useEffect(() => {
    setValue("attendeeCount", attendeeCount?.toString());
    setValue("notes", openHouse?.instructions);
  }, []);

  const saving = React.useRef(false);
  const saveDates = async () => {
    if (saving.current) return;
    saving.current = true;
    setSaveDisabled(true);
    try {
      // Validate registered fields before normalizing or submitting any values.
      await handleSubmit(async (values) => {
        const normalized = { ...values };
        for (const field of ["startTime", "endTime"]) {
          if (values.date && values[field]) {
            const combined = new Date(values.date);
            combined.setHours(values[field].getHours(), values[field].getMinutes(), 0, 0);
            normalized[field] = combined;
          }
        }
        await onSubmit(normalized);
      })();
    } catch (error) {
      Toast.show({ type: "error", text1: "Unable to end open house", text2: "Your changes are saved. Please try again." });
    } finally {
      saving.current = false;
      setSaveDisabled(false);
    }
  };

  React.useEffect(() => {
    if (openHouse) {
      setValue(
        "startTime",
        combineDateAndTime(openHouse.start_date, openHouse.start_time)
      );
      setValue(
        "endTime",
        openHouse?.end_time
          ? combineDateAndTime(openHouse.end_date, openHouse.end_time)
          : new Date()
      );
      setValue("attendeeCount", attendeeCount?.toString());
      setValue("notes", openHouse?.instructions);
    }
  }, [openHouse, attendeeCount]);

  function combineDateAndTime(dateString: string, timeString: string) {
    const [year, month, day] = dateString.split("-").map(Number);
    const [hours, minutes, seconds] = timeString.split(":").map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds);
  }

  return (
    <ScrollView
      contentContainerStyle={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      style={Platform.OS === "web" ? styles.webBody : styles.body}
      scrollEnabled={false}
    >
      <View style={styles.container}>
        <View
          transparent
          style={{
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Text
            style={{
              textAlign: "left",
              marginBottom: 16,
              color: Colors.nuetral_grey_700,
              fontWeight: "bold",
              lineHeight: 24,
              fontSize: 18,
              fontFamily: "NunitoBold",
            }}
          >
            End Open House
          </Text>
          <Text
            style={{
              color: Colors.nuetral_grey_700,
              fontFamily: "NunitoRegular",
            }}
          >
            Please confirm the duration of the open house and the number of
            attendees.
          </Text>
        </View>
        <View
          style={{
            width: "100%",
            zIndex: 2,
          }}
        >
          <DateTimeSection
            control={control}
            getValues={getValues}
            setValue={setValue}
            errors={errors}
            startTime={startTime}
            endTime={endTime}
            defaultDate={combineDateAndTime(
              openHouse?.start_date,
              openHouse?.start_time
            )}
            defaultStartTime={combineDateAndTime(
              openHouse?.start_date,
              openHouse?.start_time
            )}
            defaultEndTime={
              openHouse?.end_time
                ? combineDateAndTime(openHouse?.end_date, openHouse?.end_time)
                : new Date()
            }
            isTablet={isTablet}
            setError={setError}
            clearErrors={clearErrors}
            watch={watch}
          />
        </View>
        <View style={{ width: "100%" }}>
          <Input
            placeholder="Attendees"
            label="Attendees"
            lightColor={Colors.nuetral_grey_100}
            darkColor={Colors.nuetral_grey_700}
            control={control}
            errors={errors}
            name="attendeeCount"
            required={true}
            keyboardType="numeric"
            defaultValue={attendeeCount?.toString()}
            min={0}
          />
          <Input
            style={{
              height: 100,
            }}
            placeholder="Enter message here..."
            label="Notes"
            lightColor={Colors.tinted_grey_100}
            darkColor={Colors.nuetral_grey_700}
            control={control}
            errors={errors}
            name="notes"
            required={false}
            defaultValue={openHouse?.instructions}
            multiline
            numberOfLines={4}
          />
        </View>
      </View>
      <InputFooter
        handleSubmit={() => {
          if (!saveDisabled && Object.keys(errors).length === 0) void saveDates();
        }}
        disabled={saveDisabled || Object.keys(errors).length > 0}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  body: {
    flex: 1,
    backgroundColor: Colors.base_colors_white,
  },
  errorText: {
    color: "#d42121",
    fontSize: 12,
  },
  webBody: {
    flex: 1,
    backgroundColor: Colors.base_colors_white,
    width: "50%",
    alignSelf: "center",
    marginTop: 60,
    marginBottom: 60,
  },
  container: {
    flex: 1,
    gap: 16,
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  minus: {
    marginTop: 39,
    marginHorizontal: 10,
  },
  space: {
    width: 20,
  },
});
